<template>
  <div class="eeat-panel mt-4">
    <p class="caption text--secondary mb-4">{{ $t('seoScore.detail.eeat.intro') }}</p>
    <div class="eeat-pillars mb-4">
      <div v-for="pillar in report.pillars" :key="pillar.id" class="eeat-pillar">
        <div class="caption font-weight-bold text-uppercase eeat-pillar-label">
          {{ $t('seoScore.detail.eeat.pillars.' + pillar.id + '.name') }}
        </div>
        <div class="text-h5 font-weight-bold">{{ pillar.score }}<span class="caption">/100</span></div>
        <div class="caption mb-2">{{ $t('seoScore.detail.eeat.pillars.' + pillar.id + '.hint') }}</div>
        <v-progress-linear :value="pillar.score" height="6" rounded :color="pillarColor(pillar.score)" />
      </div>
    </div>
    <div class="geo-box mb-4">
      <div class="font-weight-medium mb-3">{{ $t('seoScore.detail.eeat.signalsTitle') }}</div>
      <div v-for="signal in report.signals" :key="signal.id" class="geo-factor">
        <v-chip x-small class="text-none mr-2 mt-1" :color="pillarChip(signal.pillar)" text-color="white">
          {{ $t('seoScore.detail.eeat.pillars.' + signal.pillar + '.name') }}
        </v-chip>
        <v-icon small :color="signal.ok ? 'success' : 'error'" class="mr-2">
          {{ signal.ok ? 'check' : 'close' }}
        </v-icon>
        <div>
          <div class="font-weight-medium">{{ $t('seoScore.detail.eeat.signals.' + signal.id + '.title') }}</div>
          <div class="caption text--secondary">
            {{ $t('seoScore.detail.eeat.signals.' + signal.id + (signal.ok ? '.ok' : '.fail'), [keyword]) }}
          </div>
        </div>
      </div>
    </div>
    <div v-if="report.actions.length" class="geo-box">
      <div class="font-weight-medium mb-3">{{ $t('seoScore.detail.eeat.actionsTitle') }}</div>
      <ol class="eeat-actions">
        <li v-for="action in report.actions" :key="action">
          <strong>{{ $t('seoScore.detail.eeat.signals.' + action + '.title') }}.</strong>
          {{ $t('seoScore.detail.eeat.signals.' + action + '.fail', [keyword]) }}
        </li>
      </ol>
    </div>
    <div class="caption text--secondary mt-3">
      {{ isAnalyze ? $t('seoScore.detail.eeat.modeAnalyze') : $t('seoScore.detail.eeat.modeWrite') }}
    </div>
  </div>
</template>

<script lang="ts">
    import Vue from "vue";
    import {evaluateEeat, EeatReport, EeatPillarId} from "@/service/geoEatScore";

    export default Vue.extend({
        name: "SeoEeatPanel",
        computed: {
            report(): EeatReport {
                return evaluateEeat(this.$store.getters.article);
            },
            keyword(): string {
                return this.$store.getters.article.selectedKeyword || this.$store.getters.article.keyword || '';
            },
            isAnalyze(): boolean {
                return !!this.$store.getters.article.isAnalyze;
            }
        },
        methods: {
            pillarColor(score: number): string {
                if (score >= 70) return 'success';
                if (score >= 45) return 'warning';
                return 'error';
            },
            pillarChip(pillar: EeatPillarId): string {
                return {
                    experience: 'info',
                    expertise: 'success',
                    authoritativeness: '#7c3aed',
                    trustworthiness: 'warning',
                }[pillar];
            }
        }
    });
</script>

<style lang="sass" scoped>
.eeat-pillars
    display: grid
    grid-template-columns: repeat(2, minmax(0, 1fr))
    gap: 12px

.eeat-pillar
    border: 1px solid #e2e8f0
    border-radius: 12px
    padding: 12px 14px
    background: rgba(255, 255, 255, 0.75)

.eeat-pillar-label
    letter-spacing: 0.06em
    color: #64748b

.geo-box
    border: 1px solid #e2e8f0
    border-radius: 12px
    padding: 14px 16px
    background: rgba(255, 255, 255, 0.7)

.geo-factor
    display: flex
    align-items: flex-start
    padding: 10px 0
    border-bottom: 1px solid #edf2f7

    &:last-child
        border-bottom: 0
        padding-bottom: 0

.eeat-actions
    margin: 0
    padding-left: 18px
    line-height: 1.5

    li + li
        margin-top: 8px

@media (max-width: 600px)
    .eeat-pillars
        grid-template-columns: 1fr

.theme--dark
    .eeat-pillar,
    .geo-box
        background: rgba(15, 23, 42, 0.35)
        border-color: #334155
</style>
